import React, { Component } from 'react';
import { update, get } from 'lodash-es';
import PropTypes from 'prop-types';
import { Button, Input } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import I18n from 'i18next';
import { getRecordData } from 'utils/tools';
import Text from '../../common/Text';
import { IconWrapper, DropdownStyles, TableStyles } from './styles';

class RestTableLayout extends Component {
  searchInput = {};

  onChangePagination = (e, filters, sorter, extra) => {
    if (extra.action === 'sort' && this.props.isSortLocal) return;
    const { resourceFilter, retrieveList } = this.props;
    const formatFilter = {};
    const formatSort =
      sorter && sorter.field && sorter.order
        ? `${sorter.order === 'descend' ? '-' : ''}${sorter.field}`
        : null;

    Object.keys(filters).forEach((filter) => {
      const filterKey = filter;
      const $in = Array.isArray(filters[filter])
        ? filters[filter].filter((data) => typeof data !== 'object')
        : filters[filter];

      const searchFilter = Array.isArray(filters[filter])
        ? filters[filter].find((data) => typeof data === 'object')
        : '';

      update(formatFilter, filterKey, () => undefined);
      if ($in?.length) {
        update(formatFilter, filterKey, () => $in);
      }
      if (searchFilter) {
        update(formatFilter, filterKey, () => searchFilter);
      } else {
        update(formatFilter, filterKey, () => $in);
      }
    });
    retrieveList({
      offset: resourceFilter.offset,
      limit: resourceFilter.limit,
      filter: { ...resourceFilter.filter, ...formatFilter },
      orderBy: formatSort,
    });
  };

  onChangeRecord = (record, item) => (value) => {
    this.props.updateRecord(
      record.id,
      {
        [item.props.source]: value,
      },
      true,
    );
  };

  handleSearch = (confirm) => {
    confirm();
  };

  handleReset = (dataIndex) => {
    const { resourceFilter, retrieveList } = this.props;
    const formatFilter = { ...resourceFilter.filter };
    update(formatFilter, dataIndex, () => undefined);
    retrieveList({
      ...resourceFilter,
      filter: { ...formatFilter },
    });
  };

  onBlur = (index, source) => (e) => {
    const { onEditHeaderSuccess } = this.props;
    onEditHeaderSuccess({ index, source, value: e.currentTarget.value });
  };

  onKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  getColumnSearchProps = (dataIndex, header, hasSearch) => {
    const { resourceFilter } = this.props;
    const defaultValue = get(resourceFilter.filter, `${dataIndex}.$like`);
    return hasSearch
      ? {
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => {
            const filters =
              selectedKeys?.filter?.((data) => typeof data === 'string') || [];
            return (
              <DropdownStyles style={{ padding: 8 }}>
                <Input
                  ref={(node) => {
                    this.searchInput[dataIndex] = node;
                  }}
                  placeholder={`Search ${I18n.t(header)}`}
                  onChange={(e) =>
                    setSelectedKeys(
                      e.target.value
                        ? [...filters, { $like: e.target.value }]
                        : filters,
                    )
                  }
                  onPressEnter={() => this.handleSearch(confirm)}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  defaultValue={defaultValue}
                />
                <Button
                  type="primary"
                  onClick={() => this.handleSearch(confirm)}
                  icon={<SearchOutlined />}
                  size="small"
                  className="search-button"
                  style={{ width: 90, marginRight: 8 }}
                >
                  {I18n.t('button.search')}
                </Button>
                <Button
                  onClick={() => {
                    this.searchInput[dataIndex].setValue('', () => {
                      this.handleReset(dataIndex, filters);
                      confirm();
                    });
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  {I18n.t('button.reset')}
                </Button>
              </DropdownStyles>
            );
          },
          filterIcon: (filtered) => (
            <IconWrapper>
              <SearchOutlined
                className={filtered || defaultValue ? 'highlightFilter' : ''}
              />
            </IconWrapper>
          ),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => this.searchInput[dataIndex].select());
            }
          },
        }
      : {};
  };

  rowKey = (data = {}, index) => data.id || index;

  render() {
    const {
      resourceData,
      children,
      resource,
      gotoEditPage,
      loading,
      onRow,
      enableOnRowDoubleClick,
      customQuery,
      resourceFilter,
      expandedRowRender,
      expandIcon,
      widthTable,
      rowClassName,
    } = this.props;

    const columns = children
      .filter((e) => e)
      .map((item) => ({
        fixed: item.props.fixed,
        title:
          item.props.source === 'actionGroup'
            ? null
            : I18n.t(item.props.header || ''),
        dataIndex: `${item.props.source}`,
        width:
          item.props.source === 'actionGroup'
            ? item.props.widthAction
            : item.props.width,
        align: item.props.align,
        key: getFilterKey(item),
        sorter: item.props.sorter
          ? (a, b) =>
              getRecordData(a, item.props.source) -
              getRecordData(b, item.props.source)
          : undefined,
        defaultSortOrder: getSorterOrder(
          resourceFilter.orderBy,
          item.props.source,
        ),
        filters: item.props.filters,
        filteredValue: (() =>
          get(resourceFilter.filter || {}, getFilterKey(item)) || [])(),
        filterMultiple: item.props.filterMultiple !== false,
        render:
          item.props.render ||
          ((obj, record) => {
            const RecordComponent = React.cloneElement(item, {
              table: true,
              record,
              loading:
                resourceData.itemLoading && resourceData.itemLoading[record.id],
              onChangeRecord: this.onChangeRecord(record, item),
              customQuery,
              modelResource: resource,
              ...getAction(this.props, item),
            });

            return <div>{RecordComponent}</div>;
          }),
        filterIcon: (filtered) => (
          <IconWrapper>
            <FilterOutlined
              className={
                filtered ||
                get(resourceFilter.filter || {}, `${item.props.source}`)
                  ? 'highlightFilter'
                  : ''
              }
            />
          </IconWrapper>
        ),
        ...(item.props.filterDropdown && {
          filterDropdown: item.props.filterDropdown(
            getFilterKey(item),
            resourceFilter,
            this.handleReset,
          ),
        }),

        ...this.getColumnSearchProps(
          item.props.source,
          item.props.header,
          item.props.hasSearch,
        ),
      }));
    return (
      <TableStyles
        {...((enableOnRowDoubleClick || onRow) && {
          onRow: (record) => ({
            onDoubleClick: () => {
              onRow ? onRow(record.id, record) : gotoEditPage(record.id);
            },
          }),
        })}
        rowClassName={rowClassName}
        onChange={this.onChangePagination}
        pagination={false}
        columns={columns}
        loading={loading}
        expandedRowRender={expandedRowRender}
        expandIcon={expandIcon}
        dataSource={resourceData || []}
        rowKey={this.rowKey}
        scroll={{ x: widthTable }}
      />
    );
  }
}

const getFilterKey = (item) =>
  item && item.props && item.props.hasSearch
    ? item.props.source
    : item.props.filterKey || `${item.props.source}.$in`;

const getSorterOrder = (orderBy, source) => {
  if (orderBy === source) return 'ascend';
  if (orderBy === `-${source}`) return 'descend';
  return undefined;
};

export const showTotal = (total, range) => (
  <Text type="button" className="txtTotal">
    {` ${range.join(' - ')}/${total} `}
  </Text>
);
export const getAction = (props, item) => {
  switch (item.props.source) {
    case 'edit':
      return { gotoEditPage: item.props.gotoEditPage || props.gotoEditPage };
    case 'delete':
      return { deleteItem: item.props.deleteItem || props.deleteItem };
    case 'show':
      return { gotoShowPage: item.props.gotoShowPage || props.gotoShowPage };
    case 'actionGroup':
    default:
      return {
        gotoShowPage: props.gotoShowPage,
        deleteItem: props.deleteItem,
        gotoEditPage: props.gotoEditPage,
      };
  }
};

RestTableLayout.propTypes = {
  children: PropTypes.node,
  retrieveList: PropTypes.func,
  gotoEditPage: PropTypes.func,
  resourceData: PropTypes.array,
  resourceFilter: PropTypes.object,
  loading: PropTypes.bool,
  updateRecord: PropTypes.func,
  onRow: PropTypes.func,
  enableOnRowDoubleClick: PropTypes.bool,
  customQuery: PropTypes.func,
  onEditHeaderSuccess: PropTypes.func,
  resource: PropTypes.string,
  expandedRowRender: PropTypes.any,
  expandIcon: PropTypes.any,
  widthTable: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]),
  rowClassName: PropTypes.func,
  isSortLocal: PropTypes.bool,
};

RestTableLayout.defaultProps = {
  onEditHeaderSuccess: () => {},
  expandedRowRender: null,
  widthTable: '1200px',
};

export default RestTableLayout;
